import { Component, type ErrorInfo, type ReactNode } from 'react'
import { clientLog } from './clientLog'

interface Props {
  children: ReactNode
  /**
   * Free-form tag for the log entry — distinguishes which boundary in the
   * tree caught the error if we ever nest more than one (e.g. "root", "admin",
   * "user").
   */
  boundary?: string
}

interface State {
  error: Error | null
}

/**
 * Catches render-time exceptions that would otherwise tear down the React
 * tree silently in production. Logs to the server via clientLog so the
 * failure shows up in journald next to fetch errors and unhandled rejections.
 *
 * Render errors do NOT fire window.onerror, so this boundary is the only
 * way to capture them. Keep the fallback UI minimal — its job is just to
 * let the user reload without being stuck on a white screen.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    clientLog.error({
      message: error.message || 'render_error',
      code: 'render_error',
      stack: error.stack,
      context: {
        boundary: this.props.boundary ?? 'unnamed',
        // componentStack is React's pseudo-stack of the component tree at the
        // point of failure — far more useful than the JS stack when trying
        // to localize which screen blew up.
        component_stack: info.componentStack ?? undefined,
      },
    })
  }

  reset = (): void => {
    this.setState({ error: null })
  }

  override render(): ReactNode {
    if (!this.state.error) return this.props.children
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
        <div className="w-full max-w-md rounded-lg border border-rose-400 bg-rose-50 p-6 dark:border-rose-500/40 dark:bg-rose-500/5">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-rose-700 dark:text-rose-300">
            something broke
          </div>
          <h1 className="mt-2 text-xl font-semibold text-slate-900 dark:text-slate-50">
            We hit an error rendering this page.
          </h1>
          <p className="mt-2 text-sm text-slate-700 dark:text-slate-300">
            The failure has been reported. Try reloading — most issues clear on a fresh load.
          </p>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-md bg-green-600 px-4 py-2 font-medium text-white transition hover:bg-green-700 dark:bg-green-500 dark:text-slate-950 dark:hover:bg-green-400"
            >
              Reload
            </button>
            <button
              type="button"
              onClick={this.reset}
              className="rounded-md border border-slate-300 bg-slate-100 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-slate-800 hover:border-emerald-500 hover:bg-slate-200 hover:text-emerald-700 dark:border-slate-700 dark:bg-transparent dark:text-slate-300 dark:hover:bg-transparent dark:hover:text-emerald-300"
            >
              Dismiss
            </button>
          </div>
          <details className="mt-4">
            <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-widest text-slate-600 hover:text-slate-900 dark:text-slate-500 dark:hover:text-slate-300">
              Show error detail
            </summary>
            <pre className="mt-2 max-h-48 overflow-auto rounded bg-slate-200/60 p-2 font-mono text-[11px] text-rose-800 dark:bg-slate-900/60 dark:text-rose-200">
              {this.state.error.message}
              {this.state.error.stack ? '\n\n' + this.state.error.stack : ''}
            </pre>
          </details>
        </div>
      </div>
    )
  }
}
